'use client'
import * as React from 'react'
import * as Dialog from '@radix-ui/react-dialog'
import { ToggleState, useToggleState } from '~/hooks/use-toggle-state'
import { MagnifyingGlassIcon } from '@radix-ui/react-icons'
import { VisuallyHidden } from '@radix-ui/react-visually-hidden'
import { useWorkingTreeStore } from '~/hooks-data/blocks-tree/provider'
import { BlockIcon } from '../block-icon'
import { clsx } from 'clsx'
import {
  parseTab,
  useTabsStore,
} from '~/app/(dash)/[team-slug]/(repo)/[repo-slug]/(editor)/explore/components/tabs/store'
import { useVirtualizer } from '@tanstack/react-virtual'
import { commandKeyPressed } from '~/lib/command-key-pressed'
import { idToSourceBlockId } from '@basehub/data/replicache/mutators/repo-space/block/mutators/copy-on-write'
import { useRepoStore } from '~/app/(dash)/[team-slug]/(repo)/[repo-slug]/(editor)/explore/components/repo-store'
import { BlockIconStoreProvider } from '../block-icon/store'
import { BlockTypes } from '@basehub/data/replicache/index'
import {
  SearchResponseBlock,
  fetchSearchResults,
} from '~/hooks/use-search-repo'
import { useQuery } from '@tanstack/react-query'
import { useRepoReplicache } from '~/hooks-data/use-repo-replicache'
import { getBlockDisplayData } from '@basehub/data/replicache/mutators/repo-space/block/helpers/component-helpers'
import { useStorage } from '~/hooks/use-storage'
import { Checkbox } from '~/sotano-kit/primitives/checkbox'
import { Badge } from '../badge'

// Unified result type for command palette
type CommandPaletteResult = {
  id: string
  sourceBlockId: string
  type: BlockTypes
  title: string
  content?: string
  queryTerms: string[]
  highlights?: Array<{
    field: string
    snippet: string
    matched_tokens: string[]
  }>
}

/* -------------------------------------------------------------------------------------------------
 * Context
 * -----------------------------------------------------------------------------------------------*/

const Context = React.createContext<undefined | { state: ToggleState }>(
  undefined
)

export const useCommandPalette = () => {
  const context = React.useContext(Context)
  if (!context) {
    throw new Error(
      'useCommandPalette must be used within a CommandPaletteProvider'
    )
  }
  return context
}

export const CommandPaletteProvider = ({
  children,
}: {
  children: React.ReactNode
}) => {
  const toggleState = useToggleState()

  React.useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // if is in a rich text editor, don't trigger the command palette
      // const target = e.target as HTMLElement | undefined
      // if (commandKeyPressed(e) && target?.closest('.ProseMirror')) {
      //   e.preventDefault()
      //   return
      // }
      if ((e.key === 'p' || e.key === 'k') && commandKeyPressed(e)) {
        e.preventDefault()
        toggleState.handleToggle()
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => {
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [toggleState])

  return (
    <Context.Provider value={{ state: toggleState }}>
      <Dialog.Root
        open={toggleState.isOn}
        onOpenChange={toggleState.handleToggle}
      >
        {children}
        <CommandPaletteDialog />
      </Dialog.Root>
    </Context.Provider>
  )
}

/* -------------------------------------------------------------------------------------------------
 * Trigger
 * -----------------------------------------------------------------------------------------------*/

export const CommandPaletteTrigger = ({
  children,
}: {
  children?: React.ReactNode
}) => {
  return <Dialog.Trigger asChild>{children}</Dialog.Trigger>
}

/* -------------------------------------------------------------------------------------------------
 * Dialog
 * -----------------------------------------------------------------------------------------------*/

const contentId = 'command-palette-dialog-content'

const CommandPaletteDialog = () => {
  const { state } = useCommandPalette()
  const [search, setSearch] = React.useState('')
  const [selectedIndex, setSelectedIndex] = React.useState(0)

  // reset state
  React.useEffect(() => {
    if (!state.isOn) {
      setSearch('')
      setSelectedIndex(0)
    }
  }, [state.isOn])

  // Get repository ID for localStorage
  const { data: repo } = useRepoReplicache()
  const storageKey = React.useMemo(() => {
    return repo?.repoId ? `command-palette-search-options-${repo.repoId}` : null
  }, [repo?.repoId])

  // Search options state
  const [searchOptions, setSearchOptions] = useStorage(
    'localStorage',
    (z) =>
      z.object({
        titleOnly: z.boolean().default(false),
      }),
    storageKey ?? '',
    { initialValue: { titleOnly: false }, enabled: !!storageKey }
  )

  return (
    <Dialog.Portal>
      <Dialog.Overlay className="fixed z-overlay inset-0 bg-dark-bg/80 animate-overlay-show" />
      <Dialog.Content
        id={contentId}
        className="fixed z-overlay top-[12%] left-1/2 max-h-[85vh] w-[90vw] max-w-[460px] shadow-modal -translate-x-1/2 rounded-lg bg-dark-base border border-dark-border-solid focus:outline-none overflow-hidden animate-content-show-no-translate"
        onCloseAutoFocus={(e) => e.preventDefault()}
      >
        <VisuallyHidden>
          <Dialog.Title>Command Palette</Dialog.Title>
        </VisuallyHidden>
        <form
          className="relative w-full border-b border-dark-border-solid bg-dark-shade"
          onSubmit={(e) => {
            e.preventDefault()
          }}
        >
          <span
            className={clsx(
              'absolute top-1/2 -translate-y-1/2 left-2 text-dark-faint pointer-events-none',
              search && '!text-dark-muted'
            )}
          >
            <MagnifyingGlassIcon />
          </span>
          <input
            className="w-full pl-8 h-10 outline-none placeholder:text-dark-faint text-dark-sm bg-transparent"
            placeholder="Search blocks"
            type="text"
            name="search"
            onChange={(e) => {
              setSearch(e.target.value)
              setSelectedIndex(0)
            }}
            value={search}
            autoComplete="off"
            autoFocus
          />
        </form>
        {state.isOn && (
          <SearchResults
            search={search}
            selectedIndex={selectedIndex}
            setSelectedIndex={setSelectedIndex}
            searchOptions={searchOptions}
            setSearchOptions={setSearchOptions}
          />
        )}
      </Dialog.Content>
    </Dialog.Portal>
  )
}

/* -------------------------------------------------------------------------------------------------
 * Search Results
 * -----------------------------------------------------------------------------------------------*/

const SearchResults = ({
  search,
  selectedIndex,
  setSelectedIndex,
  searchOptions,
  setSearchOptions,
}: {
  search: string
  selectedIndex: number
  setSelectedIndex: React.Dispatch<React.SetStateAction<number>>
  searchOptions: { titleOnly: boolean }
  setSearchOptions: (value: { titleOnly: boolean }) => void
}) => {
  const { state } = useCommandPalette()
  const treeRef = useWorkingTreeStore((s) => s.treeRef)
  const getChildrenKeys = useWorkingTreeStore((s) => s.tree?.getChildrenKeys)
  const addTab = useTabsStore((s) => s.addTab)
  const activeTab = useTabsStore((s) => s.activeTab)
  const parsedActiveTab = activeTab ? parseTab(activeTab) : null
  const setFocus = useRepoStore((s) => s.setFocus)

  const { data, isFetching } = useSearchCommandPalette({
    search,
    searchOptions,
  })
  const displayResults = data?.data ?? []
  const displayType = data?.type ?? 'recent'

  const handleSelect = React.useCallback(
    (id: string) => {
      addTab({ key: id, partiallyOpen: true })
      setFocus({
        type: 'block:document-title',
        blockId: treeRef.current ? idToSourceBlockId(id, treeRef.current) : id,
      })
      state.handleOff()
    },
    [addTab, setFocus, state, treeRef]
  )

  const parentRef = React.useRef<HTMLDivElement>(null)
  const itemHeight = 36

  const rowVirtualizer = useVirtualizer({
    count: displayResults.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => itemHeight,
    overscan: 5,
  })

  useSelectedIndexManager({
    selectedIndex,
    setSelectedIndex,
    onSelect: (index) => {
      const r = displayResults[index]
      if (r) {
        handleSelect(r.sourceBlockId || r.id)
      }
    },
    contentId,
    enabled: state.isOn,
    resultsLength: displayResults.length,
    onScrollToIndex: (index) => {
      rowVirtualizer.scrollToIndex(index, {
        align: 'auto',
      })
    },
  })

  // eslint-disable-next-line react-compiler/react-compiler
  const isOverflowing = !parentRef.current
    ? false
    : // eslint-disable-next-line react-compiler/react-compiler
      displayResults.length * itemHeight > parentRef.current.clientHeight + 4

  return (
    <BlockIconStoreProvider getChildrenKeys={getChildrenKeys}>
      {/* Progress bar container with persistent border */}
      <div className="absolute w-full z-20  h-0.5 top-10 overflow-hidden border-t border-dark-border-solid">
        {isFetching && (
          <div
            className="h-full bg-dark-accent"
            style={{
              width: '30%',
              animation: 'command-palette-loading 1.5s ease-in-out infinite',
              transformOrigin: 'left',
            }}
          />
        )}
      </div>
      <div
        className={clsx(
          isOverflowing && 'mr-0.5 pr-0.5',
          'bg-dark-base overflow-y-auto overflow-x-hidden h-[320px]'
        )}
        ref={parentRef}
      >
        {displayType === 'recent' && displayResults.length > 0 && (
          <p className="text-xs text-dark-faint font-medium pt-2 pl-2.5">
            Recent blocks
          </p>
        )}
        {/* Results or no results state */}
        {displayResults.length > 0 ? (
          <div
            className={clsx(
              isOverflowing && 'border-r border-dark-border-solid'
            )}
            style={{
              height: `${rowVirtualizer.getTotalSize()}px`,
              width: '100%',
              position: 'relative',
            }}
          >
            {rowVirtualizer.getVirtualItems().map((virtualRow) => {
              const result = displayResults[virtualRow.index]
              if (!result) return null
              const block = treeRef.current?.getNode(result.id)?.value
              const sourceBlockId = result.sourceBlockId || result.id

              const isCurrentTab = Boolean(
                parsedActiveTab?.type === 'block' &&
                  sourceBlockId === parsedActiveTab.blockId
              )
              const isSelected = virtualRow.index === selectedIndex

              const contentHighlight = result.highlights?.find(
                (h) => h.field === 'content'
              )
              const snippet =
                result.content === result.title ? '' : contentHighlight?.snippet
              return (
                <button
                  key={virtualRow.index}
                  style={{
                    position: 'absolute',
                    inset: 4,
                    width: 'calc(100% - 8px)',
                    height: `${virtualRow.size}px`,
                    transform: `translateY(${virtualRow.start}px)`,
                  }}
                  className={clsx(
                    'cursor-default flex items-center gap-2 whitespace-nowrap max-w-full w-full rounded-lg',
                    isSelected && 'dark:bg-dark-shade-hover bg-dark-shade'
                  )}
                  onPointerDown={() => {
                    handleSelect(sourceBlockId)
                  }}
                  data-search-result={true}
                  data-index={virtualRow.index}
                >
                  <div className="flex items-center">
                    <span className="text-dark-muted w-8 flex items-center justify-center">
                      <BlockIcon
                        block={{
                          type: result.type as BlockTypes,
                          id: result.id,
                        }}
                        forceNonEmptyDocumentIcon
                      />
                    </span>
                    <span
                      className={clsx(
                        'text-sm overflow-hidden text-ellipsis',
                        isSelected ? 'text-dark-bright' : 'text-dark-muted'
                      )}
                    >
                      <WithHighlightedTerms
                        text={
                          block
                            ? getBlockDisplayData({
                                block,
                                parentBlock: treeRef.current?.getNode(
                                  treeRef.current?.getNode(block.id)
                                    ?.parentKey ?? ''
                                )?.value,
                              }).displayTitle
                            : result.title || 'Untitled'
                        }
                        highlightedTerms={
                          result.highlights?.find((h) => h.field === 'title')
                            ?.matched_tokens ?? []
                        }
                        highlightedClassName="text-dark-accent font-medium"
                      />
                    </span>
                  </div>
                  <span
                    className="text-dark-faint text-xs min-w-[32px] overflow-hidden text-ellipsis pr-4 pt-px [&_mark]:!bg-transparent [&_mark]:!text-dark-accent [&_mark]:!font-medium"
                    dangerouslySetInnerHTML={{ __html: snippet ?? '' }}
                  />
                  {isCurrentTab && (
                    <Badge className="shrink-0 ml-auto mr-2">Current</Badge>
                  )}
                </button>
              )
            })}
          </div>
        ) : displayResults.length === 0 ? (
          <div className="text-dark-muted flex items-center text-xs pt-3 w-full pl-4">
            {!search ? (
              <span>Start typing to search...</span>
            ) : isFetching ? (
              <span>Searching...</span>
            ) : (
              <span>
                <span>No results</span>
                {search ? (
                  <span>
                    {' '}
                    for <span className="font-medium">"{search}"</span>
                  </span>
                ) : (
                  <span>.</span>
                )}
              </span>
            )}
          </div>
        ) : null}
      </div>
      <SearchOptionsFooter
        searchOptions={searchOptions}
        setSearchOptions={setSearchOptions}
      />
    </BlockIconStoreProvider>
  )
}

const WithHighlightedTerms = ({
  text,
  highlightedTerms,
  highlightedClassName,
  normalClassName,
}: {
  text: string
  highlightedTerms: string[]
  highlightedClassName?: string
  normalClassName?: string
}) => {
  if (highlightedTerms.length === 0) {
    return <span className={normalClassName}>{text}</span>
  }

  // Sort terms by length (longest first) to prioritize longer matches
  const sortedTerms = [...highlightedTerms].sort((a, b) => b.length - a.length)

  // Create a regex pattern that matches any of the highlighted terms (case-insensitive)
  const pattern = sortedTerms
    .map((term) => term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')) // escape special regex chars
    .join('|')

  const regex = new RegExp(`(${pattern})`, 'gi')

  // Split the text by the regex, keeping the delimiters
  const parts = text.split(regex)

  return (
    <>
      {parts.map((part, index) => {
        if (!part) return null

        // Check if this part matches any of the highlighted terms
        const isHighlighted = sortedTerms.some(
          (term) => part.toLowerCase() === term.toLowerCase()
        )

        return (
          <span
            key={index}
            className={isHighlighted ? highlightedClassName : normalClassName}
          >
            {part}
          </span>
        )
      })}
    </>
  )
}

/* -------------------------------------------------------------------------------------------------
 * Search Options Footer
 * -----------------------------------------------------------------------------------------------*/

const SearchOptionsFooter = ({
  searchOptions,
  setSearchOptions,
}: {
  searchOptions: { titleOnly: boolean }
  setSearchOptions: (value: { titleOnly: boolean }) => void
}) => {
  return (
    <div className="border-t border-dark-border-solid bg-dark-base-dark px-2.5 py-1">
      <label className="flex items-center gap-1 text-xs text-dark-muted hover:text-dark-bright transition-colors">
        <Checkbox
          checked={searchOptions.titleOnly}
          onCheckedChange={(checked) => {
            setSearchOptions({ titleOnly: checked === true })
          }}
          className="scale-75"
        />
        <span>Title only</span>
      </label>
    </div>
  )
}

/* -------------------------------------------------------------------------------------------------
 * useSelectedIndex
 * -----------------------------------------------------------------------------------------------*/

type UseSelectedIndexOptions = {
  selectedIndex: number
  setSelectedIndex: React.Dispatch<React.SetStateAction<number>>
  enabled: boolean
  onSelect: (index: number) => void
  contentId: string
  resultsLength: number
  onScrollToIndex: (index: number) => void
}

const useSelectedIndexManager = ({
  enabled,
  selectedIndex,
  setSelectedIndex,
  ...options
}: UseSelectedIndexOptions) => {
  const selectedIndexRef = React.useRef(selectedIndex)
  // eslint-disable-next-line react-compiler/react-compiler
  selectedIndexRef.current = selectedIndex

  const optionsRef = React.useRef(options)
  // eslint-disable-next-line react-compiler/react-compiler
  optionsRef.current = options

  React.useEffect(() => {
    if (!enabled) return
    const container = document.querySelector<HTMLDivElement>(
      '#' + options.contentId
    )
    if (!container) return

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowDown') {
        e.preventDefault()
        setSelectedIndex((prev) => {
          // Don't change index if there are no results
          if (optionsRef.current.resultsLength === 0) return prev

          let next = prev + 1
          if (next >= optionsRef.current.resultsLength) {
            next = optionsRef.current.resultsLength - 1
          }
          optionsRef.current.onScrollToIndex?.(next)
          return next
        })
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault()
        setSelectedIndex((prev) => {
          // Don't change index if there are no results
          if (optionsRef.current.resultsLength === 0) return prev

          let next = prev - 1
          if (next < 0) next = 0
          optionsRef.current.onScrollToIndex?.(next)
          return next
        })
      }
      if (e.key === 'Enter') {
        e.preventDefault()
        // Don't try to select if there are no results
        if (optionsRef.current.resultsLength > 0) {
          optionsRef.current.onSelect(selectedIndexRef.current)
        }
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => {
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [enabled, options.contentId, setSelectedIndex])

  React.useEffect(() => {
    if (!enabled) return
    const container = document.querySelector<HTMLDivElement>(
      '#' + options.contentId
    )
    if (!container) return

    const handleMouseMove = (e: MouseEvent) => {
      if (e.target instanceof HTMLElement) {
        const closestSearchResult = e.target.dataset.searchResult
          ? e.target
          : e.target.closest<HTMLButtonElement>(
              'button[data-search-result="true"]'
            )
        if (closestSearchResult) {
          const index = closestSearchResult.dataset.index
          if (typeof index === 'string') {
            setSelectedIndex(parseInt(index, 10))
          }
        }
      }
    }

    container.addEventListener('mousemove', handleMouseMove)
    return () => {
      container.removeEventListener('mousemove', handleMouseMove)
    }
  }, [enabled, options.contentId, setSelectedIndex])
}

const useSearchCommandPalette = ({
  search,
  searchOptions,
}: {
  search: string
  searchOptions: { titleOnly: boolean }
}) => {
  const { data: repoReplicache } = useRepoReplicache()
  const spaceId = repoReplicache?.spaceId
  const recentBlocks = useRecentBlocks()
  const rootBlockId = useWorkingTreeStore((s) => s.rootBlockId)
  const query = useQuery({
    queryFn: async () => {
      if (!spaceId || !rootBlockId) {
        return { type: 'recent', data: recentBlocks }
      }

      if (search === '') return { type: 'recent', data: recentBlocks }
      const results = formatSearchResults(
        await fetchSearchResults({
          query: search,
          spaceId,
          rootBlockId,
          by: searchOptions.titleOnly ? ['title'] : ['content', 'title'],
          limit: 10,
          searchByNearestParent: true,
          byType: searchOptions.titleOnly
            ? ['instance', 'document', 'list', 'component']
            : undefined,
        }),
        search
      )
      return { type: 'search', data: results }
    },
    enabled: !!spaceId && !!rootBlockId,
    queryKey: [
      'search-command-palette',
      search,
      spaceId,
      rootBlockId,
      searchOptions.titleOnly,
    ],
    keepPreviousData: true,
  })

  return query
}

const useRecentBlocks = (quantity = 5) => {
  const getNode = useWorkingTreeStore((s) => s.tree?.getNode)
  const lastTabOpenedHistoryMap = useTabsStore((s) => s.lastOpenedHistoryMap)

  const recentBlocks = React.useMemo((): CommandPaletteResult[] => {
    if (!getNode) return []

    const sorted = Array.from(lastTabOpenedHistoryMap.entries())
      .filter(([id]) => getNode(id))
      .sort(([, a], [, b]) => (b ?? 0) - (a ?? 0))
      .slice(0, quantity)

    return sorted.map(([id]) => {
      const block = getNode(id)?.value
      return {
        id: block!.id,
        sourceBlockId: block!.id,
        type: block!.type,
        title: block!.title || 'Untitled',
        content: '',
        queryTerms: [],
      }
    })
  }, [getNode, lastTabOpenedHistoryMap, quantity])

  return recentBlocks
}

const formatSearchResults = (
  results: SearchResponseBlock[],
  search: string
): CommandPaletteResult[] => {
  return results.map((result) => ({
    id: result.document.blockId,
    sourceBlockId: result.document.sourceBlockId || result.document.blockId,
    type: (result.document.type === 'rich-text/section'
      ? 'rich-text' // Normalize rich-text/section to rich-text
      : result.document.type) as BlockTypes,
    title: result.document.title ?? 'Untitled',
    content: Array.isArray(result.document.content)
      ? result.document.content.join(' ')
      : result.document.content,
    queryTerms: [search],
    highlights: result.highlights,
  }))
}
